import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SEO from "../../components/base/SEO";

type GalleryItem = {
  id: number;
  category: string;
  title: string;
  location: string;
  image: string;
};

const galleryItems: GalleryItem[] = [
  {
    id: 1,
    category: "Heating",
    title: "High-Efficiency Furnace Installation",
    location: "Somers, CT",
    image: "https://storage.helloreaddy.io/project_files/f6c24b4f-84f4-4d51-a5a2-519060acd333/7144debf-a542-45c8-a535-7508020212c8_compressed_937426aa-ce1f-4c8b-92b7-be7a0cc1f9bd.webp",
  },
  {
    id: 2,
    category: "Cooling",
    title: "Central AC Condenser Install",
    location: "Enfield, CT",
    image: "https://storage.helloreaddy.io/project_files/f6c24b4f-84f4-4d51-a5a2-519060acd333/bea4323b-4344-4c47-b94b-5c59bdeed410_compressed_2b5b1f5b-6a30-43f2-8afb-c433de68e265.webp",
  },
  {
    id: 3,
    category: "Heat Pumps",
    title: "Ductless Mini Split System",
    location: "Tolland, CT",
    image: "https://storage.helloreaddy.io/project_files/f6c24b4f-84f4-4d51-a5a2-519060acd333/0015f5af-e3fd-48b9-aac4-2875bc898bff_compressed_cebd2808-add4-4646-872f-546919f59235.webp",
  },
  {
    id: 4,
    category: "Commercial",
    title: "Rooftop Packaged Unit",
    location: "Vernon, CT",
    image: "https://storage.helloreaddy.io/project_files/f6c24b4f-84f4-4d51-a5a2-519060acd333/03315560-9e31-4695-a7d9-cdf0fbd56c86_compressed_5ce17455-fe17-4fca-9817-0a1452ce56bb.webp",
  },
  {
    id: 5,
    category: "Team",
    title: "Our Service Technicians",
    location: "Ultimate Heating & Cooling",
    image: "https://storage.helloreaddy.io/project_files/f6c24b4f-84f4-4d51-a5a2-519060acd333/65b59298-175b-46df-bb04-7fb0f861ab6c_compressed_the-boys.webp",
  },
];

const siteUrl: string = (import.meta.env.VITE_SITE_URL as string) ?? "";

const galleryJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "HVAC Project Gallery | Ultimate Heating & Cooling",
  description:
    "Browse recent heating, cooling, heat pump, and indoor air quality installations by Ultimate Heating & Cooling across Connecticut.",
  url: `${siteUrl}/gallery`,
  breadcrumb: {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
      { "@type": "ListItem", position: 2, name: "Gallery", item: `${siteUrl}/gallery` },
    ],
  },
};

export default function GalleryPage() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filteredItems = galleryItems;

  const openLightbox = (index: number) => setLightboxIndex(index);

  const closeLightbox = () => setLightboxIndex(null);

  const showPrev = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + filteredItems.length) % filteredItems.length);
  };

  const showNext = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % filteredItems.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="HVAC Project Gallery | Ultimate Heating & Cooling"
        description="See recent heating, cooling, heat pump, and indoor air quality installations by Ultimate Heating & Cooling. Real work from real Connecticut homes. Free estimates — call (959) 241-8524."
        keywords="HVAC project gallery, heating installation photos CT, AC installation Connecticut, heat pump install gallery, HVAC company Somers CT"
        canonical="/gallery"
        jsonLd={galleryJsonLd}
      />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20HVAC%20technician%20working%20on%20a%20modern%20furnace%20and%20air%20conditioning%20system%20in%20a%20clean%20bright%20residential%20mechanical%20room%20with%20tools%20neatly%20organized%20and%20natural%20light%20editorial%20photography%20warm%20neutral%20tones&width=1600&height=700&seq=gallery-hero-01&orientation=landscape"
          alt="Ultimate Heating & Cooling recent project work"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2447]/95 via-[#0B2447]/80 to-[#0B2447]/30"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <span className="inline-block bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 rounded-full mb-5 drop-shadow-md">
              OUR WORK
            </span>
            <h1
              className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4 max-w-2xl drop-shadow-lg"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Real Projects, Real Results
            </h1>
            <p className="text-white/80 text-base max-w-xl leading-relaxed drop-shadow-md">
              Browse recent heating, cooling, heat pump, and indoor air quality installations completed by our team across Connecticut.
            </p>
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section className="py-16 md:py-20 bg-[#F0F4FA]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredItems.map((item, idx) => (
              <button
                key={item.id}
                onClick={() => openLightbox(idx)}
                className="group relative rounded-xl overflow-hidden bg-white border border-gray-100 text-left cursor-pointer"
              >
                <div className="w-full aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt={`${item.title} — ${item.location}`}
                    title={`${item.title} — Ultimate Heating & Cooling`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                {/* Hover zoom hint */}
                <span className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-fullscreen-line text-[#0B2447] text-base"></i>
                </span>
              </button>
            ))}
          </div>

          {/* Empty state */}
          {filteredItems.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-sm">No projects in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-[#0B2447]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">READY TO JOIN OUR GALLERY?</span>
          <h2
            className="text-3xl font-extrabold text-white mt-3 mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Your Home Could Be Our Next Success Story
          </h2>
          <p className="text-gray-400 text-sm max-w-lg mx-auto mb-8 leading-relaxed">
            From full system replacements to quick repairs, we treat every project like it&apos;s our own home. Get a free estimate today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/contact"
              className="bg-[#C8102E] text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
            >
              GET A FREE ESTIMATE
            </Link>
            <a
              href="tel:9592418524"
              className="border border-white/40 text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line"></i>
              (959) 241-8524
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* ── LIGHTBOX ── */}
      {lightboxIndex !== null && filteredItems[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <div className="relative w-full max-w-4xl px-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative rounded-2xl overflow-hidden bg-black">
              <img
                src={filteredItems[lightboxIndex].image}
                alt={filteredItems[lightboxIndex].title}
                className="w-full max-h-[75vh] object-contain"
              />
              {/* Close */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-[#C8102E] rounded-full transition-colors cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-white text-xl"></i>
              </button>
              {/* Prev */}
              <button
                onClick={showPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer"
                aria-label="Previous image"
              >
                <i className="ri-arrow-left-line text-white text-xl"></i>
              </button>
              {/* Next */}
              <button
                onClick={showNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors cursor-pointer"
                aria-label="Next image"
              >
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </button>
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                <span className="inline-block bg-[#C8102E] text-white text-[9px] font-bold tracking-[0.15em] px-2.5 py-1 rounded-full mb-2">
                  {filteredItems[lightboxIndex].category.toUpperCase()}
                </span>
                <p className="text-white font-bold text-base">{filteredItems[lightboxIndex].title}</p>
                <p className="text-white/70 text-sm mt-1 flex items-center gap-1.5">
                  <i className="ri-map-pin-line"></i>
                  {filteredItems[lightboxIndex].location}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}